import classNames from 'classnames';
import {
    CSSProperties,
    Consumer,
    FC,
    MouseEventHandler,
    PropsWithChildren,
    createContext,
    useCallback,
    useMemo,
    useRef,
    useState,
} from 'react';
import style from './style.module.scss';
import {
    MAGNIFIER_CURSOR_HEIGHT,
    MAGNIFIER_CURSOR_OPACITY,
    MAGNIFIER_CURSOR_WIDTH,
    MAGNIFIER_ZOOM_POPUP_HEIGHT,
    MAGNIFIER_ZOOM_POPUP_WIDTH,
} from './constants';

interface IProps {
    zoomPopupWidth?: number;
    zoomPopupHeight?: number;
    cursorWidth?: number;
    cursorHeight?: number;
    cursorOpacity?: number;
    popupDirection?: 'left' | 'right';
}

export interface IMagnifierContext {
    cursorStyles?: CSSProperties;
    onMouseEnter?: (image: string) => MouseEventHandler<HTMLDivElement>;
}

export const MagnifierCtx = createContext<IMagnifierContext>({cursorStyles: {}});

export const MagnifierCtxConsumer: Consumer<IMagnifierContext> = MagnifierCtx.Consumer;

export const Magnifier: FC<PropsWithChildren<IProps>> = ({
    children,
    zoomPopupWidth = MAGNIFIER_ZOOM_POPUP_WIDTH,
    zoomPopupHeight = MAGNIFIER_ZOOM_POPUP_HEIGHT,
    cursorHeight = MAGNIFIER_CURSOR_HEIGHT,
    cursorWidth = MAGNIFIER_CURSOR_WIDTH,
    cursorOpacity = MAGNIFIER_CURSOR_OPACITY,
    popupDirection,
}) => {
    const magnifierWrapperRef = useRef<HTMLDivElement | null>(null);

    const [[x, y], setXY] = useState([0, 0]);
    const [[imgWidth, imgHeight], setSize] = useState([0, 0]);
    const [[imgScaledWidth, imgScaledHeight], setScaledSize] = useState([0, 0]);
    const [imgScaledSrc, setScaledImgSrc] = useState('');
    const [showMagnifier, setShowMagnifier] = useState(false);

    const zoomDiffWidth = imgWidth ? imgScaledWidth / imgWidth : 1;
    const zoomDiffHeight = imgHeight ? imgScaledHeight / imgHeight : 1;

    const handleMouseEnter = useCallback(
        (image: string) => () => {
            if (1024 > window.innerWidth) {
                return;
            }

            if (magnifierWrapperRef.current) {
                setShowMagnifier(true);
                const {width, height} = magnifierWrapperRef.current.getBoundingClientRect();
                const zoomedImg = new Image();
                zoomedImg.src = image;
                setSize([width, height]);

                zoomedImg.onload = () => {
                    const {width: zoomedWidth, height: zoomedHeight, src} = zoomedImg;
                    setScaledSize([zoomedWidth, zoomedHeight]);
                    setScaledImgSrc(src);
                };
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [magnifierWrapperRef.current],
    );

    const handleMouseMove: MouseEventHandler<HTMLDivElement> = useCallback((e) => {
        const elem = e.currentTarget;
        const {top, left} = elem.getBoundingClientRect();

        const positionX = e.pageX - left - window.scrollX;
        const positionY = e.pageY - top - window.scrollY;
        setXY([positionX, positionY]);
    }, []);

    const handleMouseLeave = useCallback(() => {
        setShowMagnifier(false);
    }, []);

    const cursorStyles = useMemo(() => {
        if (!magnifierWrapperRef.current) {
            return {};
        }

        const {width, height} = magnifierWrapperRef.current.getBoundingClientRect();
        const leftPosition = Math.max(0, Math.min(x - cursorWidth / 2, width - cursorWidth));
        const topPosition = Math.max(0, Math.min(y - cursorHeight / 2, height - cursorHeight));

        const rectStyles = {
            height: `${cursorHeight}px`,
            opacity: showMagnifier ? cursorOpacity : 0,
            transform: `translate(${leftPosition}px, ${topPosition}px)`,
            width: `${cursorWidth}px`,
        };

        return rectStyles;
    }, [cursorHeight, cursorOpacity, cursorWidth, showMagnifier, x, y]);

    const zoomPopup = useMemo(() => {
        return (
            <div
                className={classNames(style.magnifierZoomPopup, 'magnifier-zoom-popup')}
                style={{
                    backgroundImage: `url('${imgScaledSrc}')`,
                    backgroundPositionX: `${-x * zoomDiffWidth + zoomPopupWidth / 2}px`,
                    backgroundPositionY: `${-y * zoomDiffHeight + zoomPopupHeight / 2}px`,
                    backgroundSize: `${imgScaledWidth}px ${imgScaledHeight}px`,
                    left: 'left' === popupDirection ? '100%' : '-100%',
                    opacity: showMagnifier ? 1 : 0,
                }}
            />
        );
    }, [
        imgScaledSrc,
        x,
        zoomDiffWidth,
        zoomPopupWidth,
        y,
        zoomDiffHeight,
        zoomPopupHeight,
        imgScaledWidth,
        imgScaledHeight,
        popupDirection,
        showMagnifier,
    ]);

    return (
        <MagnifierCtx.Provider value={{cursorStyles, onMouseEnter: handleMouseEnter}}>
            <div
                className={classNames('magnifier-wrapper', style.magnifierWrapper)}
                onMouseLeave={handleMouseLeave}
                onMouseMove={handleMouseMove}
                ref={magnifierWrapperRef}
            >
                {children}
            </div>
            {zoomPopup}
        </MagnifierCtx.Provider>
    );
};
